'use client'

import { toast } from '@/components/common/toast'
import { SmartTooltip } from '@/components/extension/smart-tooltip'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useProductVouchers } from '@/hooks/react-query/products/use-product'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { cn } from '@/lib/utils'
import { __cAccountService } from '@/services/client/__c.account.service'
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

interface VoucherItemProps {
  discount: string
  description?: string
  code: string
  onSave?: () => void
  quantity?: number
  isPending?: boolean
}

export const ProductInfoVouchers = ({ productId }: { productId: string }) => {
  const { data: vouchers } = useProductVouchers(productId)
  const t = useTranslations()
  const { mutate: saveMutation, isPending } = useMutation({
    mutationFn: (voucher: VoucherItemProps) =>
      __cAccountService.saveVoucher(voucher.code),
    onError: (error) => {
      toast.message({
        title: t('title.voucher'),
        message: error.message || t('error.default'),
      })
    },
    onSuccess: (data, voucher) => {
      if (data.status === 'success') {
        toast.message({
          title: t('product_detail.notification.save_voucher_success', {
            code: voucher.code,
            discount: voucher.discount,
          }),
          message: data.message,
        })
      }
    },
  })

  if (!vouchers || vouchers.length === 0) return null

  const handleSaveVoucher = (voucher: VoucherItemProps) => {
    if (voucher.code) {
      saveMutation(voucher)
    }
  }

  return (
    <section className='flex items-center gap-4 py-2 lg:gap-2 lg:py-0'>
      <p className='whitespace-nowrap font-sans text-sm leading-4.5 text-neutral-600 lg:text-base lg:leading-5 lg:text-neutral-900'>
        {t('title.voucher')}
      </p>
      <div
        aria-label='Vouchers'
        className={cn('flex items-center gap-1', isPending && 'opacity-50')}
      >
        {vouchers.map((voucher, index) => (
          <ProductInfoVoucherItem
            key={`voucher-${voucher.code}-${index}`}
            discount={voucher.discount}
            description={voucher.description}
            code={voucher.code}
            quantity={voucher.quantity}
            onSave={() => handleSaveVoucher(voucher)}
            isPending={isPending}
          />
        ))}
      </div>
    </section>
  )
}

export const ProductInfoVoucherItem = ({
  discount,
  description,
  onSave,
  code,
  quantity,
  isPending,
}: VoucherItemProps) => {
  const isMobile = useIsMobileClient()
  if (isMobile) {
    return (
      <MobileVoucherItem
        discount={discount}
        description={description}
        code={code}
        quantity={quantity}
        onSave={onSave}
        isPending={isPending}
      />
    )
  }

  return (
    <DesktopVoucherItem
      discount={discount}
      description={description}
      code={code}
      quantity={quantity}
      onSave={onSave}
    />
  )
}

const VoucherTrigger = ({ discount }: { discount: string }) => {
  const t = useTranslations('product_detail.title')
  return (
    <>
      <Image
        src='/icons/icon-voucher-secondary.svg'
        alt={t('voucher_discount', { discount })}
        width={86}
        height={26}
        loading='lazy'
      />
      <p className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap px-3 font-sans text-sm font-normal leading-4.5 text-secondary'>
        {t('discount', { discount })}
      </p>
      <span className='sr-only'>{t('discount', { discount })}</span>
    </>
  )
}

const MobileVoucherItem = ({
  discount,
  description,
  onSave,
  code,
  quantity,
  isPending,
}: VoucherItemProps) => {
  const isLoggedIn = useIsLoggedIn()
  const t = useTranslations('product_detail')
  return (
    <div className='relative flex w-full justify-center'>
      <SmartTooltip
        trigger='click'
        content={
          <div className='mx-auto flex max-w-[90vw] flex-col items-center gap-2 text-center md:max-w-none'>
            <div>
              {code && quantity && (
                <div className='text-sm text-neutral-800'>
                  {t.rich('title.voucher_info', {
                    code,
                    quantity,
                    strong: (chunks) => <strong>{chunks}</strong>,
                  })}
                </div>
              )}
              {description && (
                <div className='text-sm text-neutral-800'>{description}</div>
              )}
            </div>
            {isLoggedIn && (
              <div className='md:hidden'>
                <Button
                  onClick={(event) => {
                    event.stopPropagation()
                    onSave?.()
                  }}
                  variant='black'
                  size='small'
                  className='font-pangea h-auto leading-none'
                  disabled={isPending}
                >
                  {isPending ? t('button.saving') : t('button.save_voucher')}
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div
          role='button'
          aria-label={t('title.voucher_discount', {
            discount: `${discount} ${description || ''}`,
          })}
          className='relative'
        >
          <VoucherTrigger discount={discount} />
        </div>
      </SmartTooltip>
    </div>
  )
}

const DesktopVoucherItem = ({
  discount,
  description,
  onSave,
  code,
  quantity,
}: VoucherItemProps) => {
  const t = useTranslations('product_detail.title')
  return (
    <div className='relative flex w-full justify-center'>
      <Tooltip
        content={
          <div className='flex flex-col items-center gap-2 text-center'>
            {code && quantity && (
              <p className='text-sm text-neutral-800'>
                {t.rich('voucher_info', {
                  code,
                  quantity,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </p>
            )}
            {description && (
              <p className='text-sm text-neutral-800'>{description}</p>
            )}
          </div>
        }
        side='bottom'
        trigger='hover'
      >
        <button
          type='button'
          className='relative'
          onClick={() => onSave?.()}
          aria-label={t('voucher_discount', {
            discount: `${discount} ${description || ''}`,
          })}
        >
          <VoucherTrigger discount={discount} />
        </button>
      </Tooltip>
    </div>
  )
}
